import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X, Sparkles, TrendingUp, Target, Shield, ExternalLink, Save, Check, Loader2, Mail, Linkedin, Copy, Send, MessageSquare, Search, ChevronDown, ChevronUp, Building2, User, Globe, FileText, AlertCircle, Lightbulb, HelpCircle, Zap, Play, CheckCircle2, Users, Briefcase } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import FeedbackButtons from "@/components/FeedbackButtons";

// Custom component to render formatted insights without raw markdown
const FormattedInsights = ({ content }) => {
  if (!content) return null;
  
  // Parse the content into sections
  const parseContent = (text) => {
    const sections = [];
    const lines = text.split('\n');
    let currentSection = null;
    let currentContent = [];
    
    const sectionPatterns = [
      { regex: /^\*?\*?❓?\s*HELP ME BUILD|QUESTION FOR YOU/i, type: 'question', icon: HelpCircle, color: 'orange' },
      { regex: /^\*?\*?🎯?\s*PROSPECT|LEAD|FOUND/i, type: 'prospect', icon: Target, color: 'green' },
      { regex: /^\*?\*?⚠️?\s*COMPETITOR|THREAT/i, type: 'threat', icon: AlertCircle, color: 'red' },
      { regex: /^\*?\*?💡?\s*WHY THIS MATTERS|INSIGHT/i, type: 'insight', icon: Lightbulb, color: 'blue' },
      { regex: /^\*?\*?⚡?\s*IMMEDIATE ACTION|ACTION|NEXT STEP/i, type: 'action', icon: Zap, color: 'teal' },
      { regex: /^\*?\*?📊?\s*MARKET|RESEARCH|DATA/i, type: 'research', icon: TrendingUp, color: 'blue' },
      { regex: /^\*?\*?🛡️?\s*STRATEGY|RECOMMENDATION/i, type: 'strategy', icon: Shield, color: 'orange' },
    ];
    
    lines.forEach(line => {
      const cleanLine = line.replace(/^\*\*|\*\*$/g, '').trim();
      
      // Check if this line starts a new section
      let isNewSection = false;
      for (const pattern of sectionPatterns) {
        if (pattern.regex.test(cleanLine)) {
          // Save previous section
          if (currentSection) {
            sections.push({ ...currentSection, content: currentContent.join('\n').trim() });
          }
          currentSection = { 
            title: cleanLine.replace(/^\*\*|\*\*$/g, '').replace(/^[❓🎯⚠️💡⚡📊🛡️]\s*/, ''),
            type: pattern.type,
            Icon: pattern.icon,
            color: pattern.color
          };
          currentContent = [];
          isNewSection = true;
          break;
        }
      }
      
      if (!isNewSection && cleanLine) {
        // Clean up bullet points and asterisks
        let processedLine = cleanLine
          .replace(/^\*\s+/, '')  // Remove leading asterisk bullets
          .replace(/^•\s*/, '')   // Remove bullet points
          .replace(/^\*\*|\*\*$/g, '') // Remove bold markers
          .replace(/\*\*/g, '');  // Remove remaining bold markers
        
        if (processedLine) {
          currentContent.push(processedLine);
        }
      }
    });
    
    // Don't forget the last section
    if (currentSection) {
      sections.push({ ...currentSection, content: currentContent.join('\n').trim() });
    }
    
    // If no sections were parsed, return the content as a single block
    if (sections.length === 0) {
      return [{ 
        title: null, 
        content: text.replace(/\*\*/g, '').replace(/^\*\s+/gm, '• '),
        type: 'default',
        Icon: null,
        color: 'gray'
      }];
    }
    
    return sections;
  };
  
  const sections = parseContent(content);
  
  const getColorClasses = (color) => {
    switch (color) {
      case 'orange': return { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', icon: 'text-orange-400' };
      case 'green': return { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', icon: 'text-green-400' };
      case 'red': return { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', icon: 'text-red-400' };
      case 'blue': return { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', icon: 'text-blue-400' };
      case 'teal': return { bg: 'bg-teal-500/10', border: 'border-teal-500/30', text: 'text-teal-400', icon: 'text-teal-400' };
      default: return { bg: 'bg-gray-500/10', border: 'border-gray-500/30', text: 'text-gray-300', icon: 'text-gray-400' };
    }
  };
  
  return (
    <div className="space-y-4">
      {sections.map((section, idx) => {
        const colors = getColorClasses(section.color);
        const Icon = section.Icon;
        
        return (
          <div 
            key={idx} 
            className={`${colors.bg} ${colors.border} border rounded-lg p-4`}
          >
            {section.title && (
              <div className="flex items-center gap-2 mb-3">
                {Icon && <Icon className={`h-5 w-5 ${colors.icon}`} />}
                <h4 className={`font-semibold ${colors.text} text-sm uppercase tracking-wide`}>
                  {section.title}
                </h4>
              </div>
            )}
            <div className="text-gray-300 text-sm leading-relaxed space-y-2">
              {section.content.split('\n').map((line, lineIdx) => (
                <p key={lineIdx} className={line.startsWith('•') ? 'pl-4' : ''}>
                  {line}
                </p>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const DetailedInsightsModal = ({ isOpen, onClose, activityData, userId, onStrategyExecuted }) => {
  const [savingLead, setSavingLead] = useState(null);
  const [savedLeads, setSavedLeads] = useState(new Set());
  const [generatingOutreach, setGeneratingOutreach] = useState(null);
  const [outreachData, setOutreachData] = useState({});
  const [sendingEmail, setSendingEmail] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [expandedLeads, setExpandedLeads] = useState(new Set());
  const [loadingDetails, setLoadingDetails] = useState(null);
  const [leadDetails, setLeadDetails] = useState({});
  const [executingStrategy, setExecutingStrategy] = useState(false);
  const [strategyResults, setStrategyResults] = useState(null);

  // Execute Strategy - takes the guidance and has the agent act on it
  const handleExecuteStrategy = async () => {
    if (executingStrategy) return;
    
    setExecutingStrategy(true);
    setStrategyResults(null);
    
    try {
      const response = await axios.post(`${API}/execute-strategy`, {
        userId,
        strategyType: activityData.type,
        insights: activityData.insights,
        summary: activityData.summary,
        context: {
          hasQuestion: activityData.hasQuestion,
          actionId: activityData.actionId
        }
      });
      
      if (response.data.success) {
        setStrategyResults(response.data);
        toast.success(response.data.message || "Strategy executed successfully!");
        // Notify parent that strategy was executed
        if (onStrategyExecuted) {
          onStrategyExecuted();
        }
      } else {
        toast.error(response.data.message || "Failed to execute strategy");
      }
    } catch (error) {
      console.error("Error executing strategy:", error);
      toast.error("Failed to execute strategy. Please try again.");
    } finally {
      setExecutingStrategy(false);
    }
  };

  const toggleExpand = async (idx, company) => {
    if (expandedLeads.has(idx)) {
      setExpandedLeads(prev => {
        const newSet = new Set(prev);
        newSet.delete(idx);
        return newSet;
      });
    } else {
      setExpandedLeads(prev => new Set([...prev, idx]));
      
      // Fetch more details if not already loaded
      if (!leadDetails[idx]) {
        setLoadingDetails(idx);
        try {
          // Search for more details about this company/person
          const searchQuery = company.personName 
            ? `${company.personName} ${company.companyName || ''} background experience`
            : `${company.companyName || company.title} company overview`;
          
          const response = await axios.post(`${API}/web-search`, {
            query: searchQuery,
            numResults: 3
          });
          
          if (response.data.results) {
            setLeadDetails(prev => ({
              ...prev,
              [idx]: {
                additionalInfo: response.data.results,
                fetchedAt: new Date().toISOString()
              }
            }));
          }
        } catch (error) {
          console.error("Error fetching details:", error);
          // Still expand, just without additional info
          setLeadDetails(prev => ({
            ...prev,
            [idx]: { additionalInfo: [], error: true }
          }));
        } finally {
          setLoadingDetails(null);
        }
      }
    }
  };

  const handleSaveLead = async (company, index) => {
    setSavingLead(index);
    try {
      const leadData = {
        userId: userId,
        companyName: company.companyName || company.title?.split(' - ')[1]?.split(' at ')?.[1] || company.title || "Unknown Company",
        contactName: company.personName || null,
        contactTitle: company.personTitle || null,
        linkedinUrl: company.url?.includes('linkedin.com') ? company.url : null,
        website: !company.url?.includes('linkedin.com') ? company.url : null,
        industry: "Consulting",
        source: "autonomous_stream",
        sourceUrl: company.url,
        notes: company.snippet || company.title
      };

      await axios.post(`${API}/leads`, leadData);
      setSavedLeads(prev => new Set([...prev, index]));
      toast.success(`Lead saved to CRM!`);
    } catch (error) {
      console.error("Error saving lead:", error);
      toast.error("Failed to save lead");
    } finally {
      setSavingLead(null);
    }
  };

  const handleGenerateOutreach = async (company, index) => {
    setGeneratingOutreach(index);
    try {
      const response = await axios.post(`${API}/generate-outreach`, {
        userId: userId,
        contactName: company.personName || "Decision Maker",
        contactTitle: company.personTitle || "Executive",
        companyName: company.companyName || company.title?.split(' at ')?.[1] || "Company",
        companyUrl: company.url,
        linkedinUrl: company.url?.includes('linkedin.com') ? company.url : null,
        triggerEvent: company.snippet || "ICP match"
      });

      if (response.data.success) {
        setOutreachData(prev => ({
          ...prev,
          [index]: response.data
        }));
        toast.success("Outreach generated!");
      }
    } catch (error) {
      console.error("Error generating outreach:", error);
      toast.error("Failed to generate outreach");
    } finally {
      setGeneratingOutreach(null);
    }
  };

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const handleSendEmail = async (outreach, company, index) => {
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/send-email`, {
        userId: userId,
        recipientEmail: outreach.email?.recipientEmail || "",
        recipientName: outreach.email?.recipientName || company.personName || "Prospect",
        subject: outreach.email?.subject || "Introduction",
        body: outreach.email?.body || ""
      });

      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const getModeIcon = () => {
    switch (activityData?.type) {
      case "prospect": return <Target className="h-6 w-6 text-green-400" />;
      case "research": return <Search className="h-6 w-6 text-blue-400" />;
      case "strategy": return <TrendingUp className="h-6 w-6 text-orange-400" />;
      case "competitor": return <Shield className="h-6 w-6 text-red-400" />;
      default: return <Sparkles className="h-6 w-6 text-[#3dd9c5]" />;
    }
  };

  const getModeTitle = () => {
    switch (activityData?.type) {
      case "prospect": return "Prospect Discovery";
      case "research": return "Market Intelligence";
      case "strategy": return "Strategy Analysis";
      case "competitor": return "Competitive Alert";
      default: return "AI Insights";
    }
  };

  const getModeBadgeColor = () => {
    switch (activityData?.type) {
      case "prospect": return "bg-green-500/20 text-green-400 border-green-500/30";
      case "research": return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "strategy": return "bg-orange-500/20 text-orange-400 border-orange-500/30";
      case "competitor": return "bg-red-500/20 text-red-400 border-red-500/30";
      default: return "bg-[#3dd9c5]/20 text-[#3dd9c5] border-[#3dd9c5]/30";
    }
  };

  if (!isOpen || !activityData) return null;

  const companiesFound = activityData.companiesFound || [];
  const newsItems = activityData.newsItems || [];
  const hasCompanyData = companiesFound.length > 0 || newsItems.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
      <Card className="relative w-full max-w-3xl max-h-[90vh] bg-[#0f1419] border-gray-800 overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-[#0f1419] to-[#1a1f24] border-b border-gray-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getModeIcon()}
              <div>
                <h2 className="text-lg font-bold text-white">{getModeTitle()}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`${getModeBadgeColor()} border text-xs`}>
                    {activityData.type?.toUpperCase()}
                  </Badge>
                  {activityData.totalResults > 0 && (
                    <Badge className="bg-[#3dd9c5]/20 text-[#3dd9c5] border-[#3dd9c5]/30 text-xs">
                      {activityData.totalResults} found
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Feedback Buttons */}
              <FeedbackButtons
                userId={userId}
                agentType="autonomous_stream"
                actionId={activityData?.actionId}
                outputSummary={activityData?.summary}
                context={{ type: activityData?.type }}
              />
              <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <ScrollArea className="h-[calc(90vh-180px)]">
          <div className="p-6 space-y-6">
            {/* Summary - only show for non-prospect types or if no company data */}
            {(activityData.type !== "prospect" || !hasCompanyData) && activityData.summary && (
              <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-200 text-sm leading-relaxed">{activityData.summary}</p>
              </div>
            )}

            {/* Prospect Companies Found */}
            {activityData.type === "prospect" && hasCompanyData && (
              <>
                {/* LinkedIn Profiles / Companies */}
                {companiesFound.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-[#3dd9c5] uppercase tracking-wide mb-4 flex items-center gap-2">
                      <Linkedin className="h-4 w-4" />
                      Prospects Found ({companiesFound.length})
                    </h3>
                    <div className="space-y-4">
                      {companiesFound.map((company, idx) => (
                        <div key={idx} className="bg-white/5 rounded-lg border border-gray-800 overflow-hidden">
                          {/* Main Card Header */}
                          <div className="p-4">
                            {/* Company/Person Info with ICP Score */}
                            <div className="flex items-start justify-between gap-4 mb-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  {company.personName && (
                                    <h4 className="text-white font-semibold">{company.personName}</h4>
                                  )}
                                  {/* ICP Match Score Badge */}
                                  {company.icpMatch && (
                                    <Badge 
                                      variant="outline" 
                                      className={`text-xs px-2 py-0.5 ${
                                        company.icpMatch.matchLevel === 'high' 
                                          ? 'bg-green-500/20 border-green-500 text-green-400' 
                                          : company.icpMatch.matchLevel === 'medium'
                                          ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
                                          : 'bg-red-500/20 border-red-500 text-red-400'
                                      }`}
                                      data-testid={`icp-score-${idx}`}
                                    >
                                      <Target className="h-3 w-3 mr-1" />
                                      {company.icpMatch.score}% ICP
                                    </Badge>
                                  )}
                                </div>
                                {company.personTitle && (
                                  <p className="text-gray-400 text-sm">{company.personTitle}</p>
                                )}
                                {company.companyName && (
                                  <p className="text-[#3dd9c5] text-sm">at {company.companyName}</p>
                                )}
                                {!company.personName && company.title && (
                                  <div className="flex items-center gap-2">
                                    <h4 className="text-white font-semibold text-sm line-clamp-2">{company.title}</h4>
                                    {/* ICP Match Score Badge for non-person leads */}
                                    {company.icpMatch && (
                                      <Badge 
                                        variant="outline" 
                                        className={`text-xs px-2 py-0.5 flex-shrink-0 ${
                                          company.icpMatch.matchLevel === 'high' 
                                            ? 'bg-green-500/20 border-green-500 text-green-400' 
                                            : company.icpMatch.matchLevel === 'medium'
                                            ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
                                            : 'bg-red-500/20 border-red-500 text-red-400'
                                        }`}
                                        data-testid={`icp-score-${idx}`}
                                      >
                                        <Target className="h-3 w-3 mr-1" />
                                        {company.icpMatch.score}% ICP
                                      </Badge>
                                    )}
                                  </div>
                                )}
                                {/* ICP Issues (if any) */}
                                {company.icpMatch?.issues?.length > 0 && (
                                  <div className="mt-1">
                                    {company.icpMatch.issues.map((issue, i) => (
                                      <span key={i} className="text-xs text-orange-400/70 mr-2">
                                        ⚠️ {issue}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              
                              {/* Action Buttons */}
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {/* Feedback Buttons for each lead */}
                                <FeedbackButtons
                                  userId={userId}
                                  agentType="prospect"
                                  actionId={activityData?.actionId}
                                  outputSummary={company.personName || company.title}
                                  context={{ 
                                    leadIndex: idx, 
                                    companyName: company.companyName,
                                    type: "prospect_lead"
                                  }}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSaveLead(company, idx)}
                                  disabled={savingLead === idx || savedLeads.has(idx)}
                                  className={`${savedLeads.has(idx) ? 'bg-green-600/20 border-green-600 text-green-400' : 'border-gray-700 text-white hover:bg-white/10'}`}
                                  data-testid={`save-prospect-${idx}`}
                                >
                                  {savingLead === idx ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : savedLeads.has(idx) ? (
                                    <Check className="h-3 w-3" />
                                  ) : (
                                    <Save className="h-3 w-3" />
                                  )}
                                  <span className="ml-1 text-xs">{savedLeads.has(idx) ? 'Saved' : 'Save'}</span>
                                </Button>
                              </div>
                            </div>

                            {/* Snippet Preview */}
                            {company.snippet && (
                              <p className="text-gray-400 text-sm mb-3 line-clamp-2">{company.snippet}</p>
                            )}

                            {/* URL */}
                            {company.url && (
                              <a
                                href={company.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#3dd9c5] text-xs hover:underline flex items-center gap-1 mb-3"
                              >
                                <ExternalLink className="h-3 w-3" />
                                {company.url.length > 50 ? company.url.substring(0, 50) + '...' : company.url}
                              </a>
                            )}

                            {/* Action Row: Expand Details + Generate Outreach */}
                            <div className="flex items-center gap-2 mt-3">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => toggleExpand(idx, company)}
                                className="border-gray-700 text-gray-300 hover:bg-white/10"
                                data-testid={`expand-details-${idx}`}
                              >
                                {loadingDetails === idx ? (
                                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                ) : expandedLeads.has(idx) ? (
                                  <ChevronUp className="h-3 w-3 mr-1" />
                                ) : (
                                  <ChevronDown className="h-3 w-3 mr-1" />
                                )}
                                <span className="text-xs">{expandedLeads.has(idx) ? 'Less' : 'More Details'}</span>
                              </Button>
                              
                              {!outreachData[idx] && (
                                <Button
                                  size="sm"
                                  onClick={() => handleGenerateOutreach(company, idx)}
                                  disabled={generatingOutreach === idx}
                                  className="bg-[#e91e8c] hover:bg-[#d01a7d] text-white"
                                  data-testid={`generate-outreach-${idx}`}
                                >
                                  {generatingOutreach === idx ? (
                                    <>
                                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                      Generating...
                                    </>
                                  ) : (
                                    <>
                                      <MessageSquare className="h-3 w-3 mr-1" />
                                      Generate Outreach
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Expanded Details Section */}
                          {expandedLeads.has(idx) && (
                            <div className="border-t border-gray-700 bg-black/20 p-4">
                              <h5 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                                <FileText className="h-4 w-4 text-[#3dd9c5]" />
                                Extended Information
                              </h5>
                              
                              {/* Full Snippet */}
                              {company.snippet && (
                                <div className="mb-4">
                                  <p className="text-xs text-gray-500 uppercase mb-1">Description</p>
                                  <p className="text-gray-300 text-sm">{company.snippet}</p>
                                </div>
                              )}
                              
                              {/* Company Details Grid */}
                              <div className="grid grid-cols-2 gap-4 mb-4">
                                {company.personName && (
                                  <div>
                                    <p className="text-xs text-gray-500 uppercase mb-1 flex items-center gap-1">
                                      <User className="h-3 w-3" /> Contact
                                    </p>
                                    <p className="text-white text-sm">{company.personName}</p>
                                    {company.personTitle && (
                                      <p className="text-gray-400 text-xs">{company.personTitle}</p>
                                    )}
                                  </div>
                                )}
                                {company.companyName && (
                                  <div>
                                    <p className="text-xs text-gray-500 uppercase mb-1 flex items-center gap-1">
                                      <Building2 className="h-3 w-3" /> Company
                                    </p>
                                    <p className="text-white text-sm">{company.companyName}</p>
                                  </div>
                                )}
                                {company.url && (
                                  <div>
                                    <p className="text-xs text-gray-500 uppercase mb-1 flex items-center gap-1">
                                      <Globe className="h-3 w-3" /> Source
                                    </p>
                                    <a
                                      href={company.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[#3dd9c5] text-xs hover:underline break-all"
                                    >
                                      {company.url}
                                    </a>
                                  </div>
                                )}
                                <div>
                                  <p className="text-xs text-gray-500 uppercase mb-1">Source Type</p>
                                  <Badge className={`${company.source === 'linkedin' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'} text-xs`}>
                                    {company.source === 'linkedin' ? 'LinkedIn' : 'Web'}
                                  </Badge>
                                </div>
                              </div>
                              
                              {/* Additional Web Search Results */}
                              {leadDetails[idx]?.additionalInfo?.length > 0 && (
                                <div>
                                  <p className="text-xs text-gray-500 uppercase mb-2 flex items-center gap-1">
                                    <Search className="h-3 w-3" /> Additional Research
                                  </p>
                                  <div className="space-y-2">
                                    {leadDetails[idx].additionalInfo.map((info, infoIdx) => (
                                      <div key={infoIdx} className="bg-white/5 rounded p-2">
                                        <a
                                          href={info.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-[#3dd9c5] text-xs hover:underline font-medium"
                                        >
                                          {info.title}
                                        </a>
                                        {info.snippet && (
                                          <p className="text-gray-400 text-xs mt-1 line-clamp-2">{info.snippet}</p>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {loadingDetails === idx && (
                                <div className="flex items-center gap-2 text-gray-500 text-sm">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Loading additional details...
                                </div>
                              )}
                            </div>
                          )}

                          {/* Outreach Content */}
                          {outreachData[idx] && (
                            <div className="mt-4 space-y-4">
                              {/* Email Outreach */}
                              <div className="bg-black/30 rounded-lg p-4 border border-gray-700">
                                <div className="flex items-center justify-between mb-3">
                                  <h5 className="text-white font-semibold flex items-center gap-2">
                                    <Mail className="h-4 w-4 text-blue-400" />
                                    Email Outreach
                                  </h5>
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleCopy(
                                        `Subject: ${outreachData[idx].email?.subject}\n\n${outreachData[idx].email?.body}`,
                                        `email-${idx}`
                                      )}
                                      className="border-gray-700 text-white hover:bg-white/10"
                                    >
                                      {copiedField === `email-${idx}` ? (
                                        <Check className="h-3 w-3 text-green-400" />
                                      ) : (
                                        <Copy className="h-3 w-3" />
                                      )}
                                      <span className="ml-1 text-xs">Copy</span>
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => handleSendEmail(outreachData[idx], company, idx)}
                                      disabled={sendingEmail}
                                      className="bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                      {sendingEmail ? (
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                      ) : (
                                        <Send className="h-3 w-3" />
                                      )}
                                      <span className="ml-1 text-xs">Send</span>
                                    </Button>
                                  </div>
                                </div>
                                {outreachData[idx].email?.subject && (
                                  <div className="mb-3 pb-2 border-b border-gray-700">
                                    <span className="text-gray-500 text-xs">Subject: </span>
                                    <span className="text-gray-300 text-sm">{outreachData[idx].email.subject}</span>
                                  </div>
                                )}
                                <div className="text-gray-300 text-sm whitespace-pre-wrap break-words">
                                  {outreachData[idx].email?.body}
                                </div>
                              </div>

                              {/* LinkedIn Outreach */}
                              <div className="bg-black/30 rounded-lg p-4 border border-gray-700">
                                <div className="flex items-center justify-between mb-3">
                                  <h5 className="text-white font-semibold flex items-center gap-2">
                                    <Linkedin className="h-4 w-4 text-blue-500" />
                                    LinkedIn Outreach
                                  </h5>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleCopy(
                                      outreachData[idx].linkedin?.followUpMessage || outreachData[idx].linkedin?.connectionRequest,
                                      `linkedin-${idx}`
                                    )}
                                    className="border-gray-700 text-white hover:bg-white/10"
                                  >
                                    {copiedField === `linkedin-${idx}` ? (
                                      <Check className="h-3 w-3 text-green-400" />
                                    ) : (
                                      <Copy className="h-3 w-3" />
                                    )}
                                    <span className="ml-1 text-xs">Copy</span>
                                  </Button>
                                </div>
                                <div className="text-gray-300 text-sm whitespace-pre-wrap break-words">
                                  {outreachData[idx].linkedin?.followUpMessage || outreachData[idx].linkedin?.connectionRequest}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* News / Trigger Events - Blue for news/research */}
                {newsItems.length > 0 && (
                  <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                    <h3 className="text-sm font-bold text-blue-400 uppercase tracking-wide mb-4 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      📰 News & Trigger Events ({newsItems.length})
                    </h3>
                    <p className="text-gray-400 text-xs mb-4">Recent news that may indicate buying signals or outreach opportunities</p>
                    <div className="space-y-3">
                      {newsItems.map((news, idx) => (
                        <div key={idx} className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
                          <h4 className="text-blue-300 text-sm font-medium">{news.title}</h4>
                          <p className="text-gray-400 text-xs mt-1 line-clamp-2">{news.snippet}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-blue-400/60 text-xs">{news.source}</span>
                            {news.url && (
                              <a
                                href={news.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 text-xs hover:underline flex items-center gap-1"
                              >
                                <ExternalLink className="h-3 w-3" />
                                Read Article
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Non-prospect content (research, strategy, competitor) */}
            {activityData.type !== "prospect" && activityData.insights && (
              <>
                <FormattedInsights content={activityData.insights} />
                
                {/* Execute Strategy Button */}
                {(activityData.type === "strategy" || activityData.type === "research" || activityData.type === "competitor") && (
                  <div className="mt-6">
                    {!strategyResults ? (
                      <div className="bg-gradient-to-r from-[#e91e8c]/20 to-[#3dd9c5]/20 rounded-lg p-4 border border-[#e91e8c]/30">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-white font-semibold flex items-center gap-2">
                              <Play className="h-5 w-5 text-[#e91e8c]" />
                              Ready to Act?
                            </h4>
                            <p className="text-gray-400 text-sm mt-1">
                              Let the agent execute this strategy for you
                            </p>
                          </div>
                          <Button
                            onClick={handleExecuteStrategy}
                            disabled={executingStrategy}
                            className="bg-[#e91e8c] hover:bg-[#d01a7d] text-white px-6"
                            data-testid="execute-strategy-btn"
                          >
                            {executingStrategy ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Executing...
                              </>
                            ) : (
                              <>
                                <Play className="h-4 w-4 mr-2" />
                                Execute Strategy
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
                        <div className="flex items-center gap-2 mb-4">
                          <CheckCircle2 className="h-5 w-5 text-green-400" />
                          <h4 className="text-green-400 font-semibold">Strategy Executed</h4>
                        </div>
                        
                        {/* Show execution results */}
                        {strategyResults.actionsTaken && strategyResults.actionsTaken.length > 0 && (
                          <div className="mb-4">
                            <p className="text-gray-400 text-xs uppercase mb-2">Actions Taken</p>
                            <ul className="space-y-2">
                              {strategyResults.actionsTaken.map((action, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                                  <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                                  {action}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Show generated leads if any */}
                        {strategyResults.leadsGenerated && strategyResults.leadsGenerated.length > 0 && (
                          <div className="mb-4">
                            <p className="text-gray-400 text-xs uppercase mb-2 flex items-center gap-2">
                              <Users className="h-4 w-4" />
                              Leads Generated ({strategyResults.leadsGenerated.length})
                            </p>
                            <div className="space-y-2">
                              {strategyResults.leadsGenerated.slice(0, 3).map((lead, idx) => (
                                <div key={idx} className="bg-white/5 rounded p-2 text-sm">
                                  <span className="text-white font-medium">{lead.name}</span>
                                  {lead.title && <span className="text-gray-400"> - {lead.title}</span>}
                                  {lead.company && <span className="text-[#3dd9c5]"> at {lead.company}</span>}
                                </div>
                              ))}
                              {strategyResults.leadsGenerated.length > 3 && (
                                <p className="text-gray-500 text-xs">+{strategyResults.leadsGenerated.length - 3} more leads saved to Accounts</p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Show next steps */}
                        {strategyResults.nextSteps && (
                          <div className="mt-4 pt-4 border-t border-green-500/20">
                            <p className="text-gray-400 text-xs uppercase mb-2">Recommended Next Steps</p>
                            <p className="text-gray-300 text-sm">{strategyResults.nextSteps}</p>
                          </div>
                        )}
                        
                        {/* Reset button */}
                        <Button
                          onClick={() => setStrategyResults(null)}
                          variant="outline"
                          size="sm"
                          className="mt-4 border-green-500/30 text-green-400 hover:bg-green-500/10"
                        >
                          Execute Another Strategy
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* No data message */}
            {activityData.type === "prospect" && !hasCompanyData && (
              <div className="text-center py-8">
                <Search className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No prospects found in this search.</p>
                <p className="text-gray-500 text-sm mt-2">Try using Hunt mode for detailed lead generation.</p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="sticky bottom-0 bg-[#0f1419] border-t border-gray-800 px-6 py-4">
          <div className="flex gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 bg-transparent border-gray-700 text-white hover:bg-white/10"
            >
              Close
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default DetailedInsightsModal;
